import createCache from "@emotion/cache";
import type { EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import type { ReactNode } from "react";
import ReactDOM from "react-dom/client";
import { Sentry } from "@/instrument";
import { env } from "@/env";
import type { WidgetRuntimeConfig } from "@/context/widget-runtime-config";
import {
  WidgetRuntimeConfigProvider,
} from "@/context/widget-runtime-config";
import { initializeLangfuseClient } from "@/services/langfuse-client";
import { DEFAULT_DISCLAIMER_TEXT } from "@/constants/disclaimer";
import { resolveWidgetContent } from "./resolve-widget-content";
import type { WidgetTheme } from "./widget-runtime";
import {
  applyThemeVariables,
  applyThemeWithOverrides,
  fetchWidgetTheme,
  injectBaseStyles,
  readWidgetAttributes,
} from "./widget-runtime";

export type BootstrapOptions = {
  /** Identifier used in Sentry tags + console warnings. */
  componentName: string;
  /**
   * Render function — receives the current runtime config and returns the
   * React tree to render inside the shadow root. Called on initial mount
   * and again after the theme fetch resolves (so context consumers see the
   * tenant config).
   */
  render: (config: WidgetRuntimeConfig) => ReactNode;
};

export type BootstrapHandle = {
  unmount: () => void;
};

/**
 * Common bootstrap dance for every drawer-widget custom element:
 *   1. Inject base styles, including the reset scoped to this shadow root.
 *   2. Read all HTML attributes (api-key, api-url, 6 color overrides).
 *   3. Validate api-key (early return on missing key — widget silently does nothing).
 *   4. Seed an initial runtime config from local defaults.
 *   5. Apply color attribute overrides as CSS variables immediately.
 *   6. Mount the React tree behind a runtime-config provider.
 *   7. Fire-and-forget GET /v2/widget-config (Bearer auth), then merge the
 *      tenant config into runtime config + langfuse client and re-render.
 *
 * Returns a handle that the host element can call from `disconnectedCallback`.
 */
export const bootstrapWidgetWebComponent = (
  element: HTMLElement,
  options: BootstrapOptions,
): BootstrapHandle | null => {
  if (!element.shadowRoot) {
    return null;
  }

  injectBaseStyles(element);

  const attributes = readWidgetAttributes(element);
  if (!attributes.apiKey) {
    console.error(
      `Pollen Drawer Widget (${options.componentName}): "api-key" attribute is required.`,
    );
    return null;
  }

  const apiUrl = attributes.apiUrl ?? env.API_URL;
  if (!apiUrl) {
    console.error(
      `Pollen Drawer Widget (${options.componentName}): "api-url" attribute is required (or set VITE_API_URL at build time).`,
    );
    return null;
  }

  const inlineContent = {
    disclaimerText: attributes.disclaimerText,
    suggestedQuestions: attributes.suggestedQuestions,
  };
  const initialContent = resolveWidgetContent({
    inline: inlineContent,
    tenant: {},
    defaultDisclaimerText: DEFAULT_DISCLAIMER_TEXT,
  });

  let runtimeConfig: WidgetRuntimeConfig = {
    apiKey: attributes.apiKey,
    apiUrl,
    showChatQuestions: true,
    showLandingQuestions: true,
    widgetDisclaimerText: initialContent.disclaimerText,
    widgetSuggestedQuestions: initialContent.suggestedQuestions,
    langfuse: null,
  };

  const colorOverrides: WidgetTheme = {
    textColor: attributes.textColor,
    backgroundColor: attributes.backgroundColor,
    primaryColor: attributes.primaryColor,
    cardBackgroundColor: attributes.cardBackgroundColor,
    textSecondaryColor: attributes.textSecondaryColor,
    borderColor: attributes.borderColor,
    fontFamily: attributes.fontFamily,
    headingFontFamily: attributes.headingFontFamily,
    accentColor: attributes.accentColor,
    iconAccentColor: attributes.iconAccentColor,
    inputBorderColor: attributes.inputBorderColor,
    inputBackgroundColor: attributes.inputBackgroundColor,
  };

  applyThemeVariables(element, colorOverrides, true);

  const cache: EmotionCache = createCache({
    key: "shadow-emotion",
    container: element.shadowRoot as unknown as Element,
  });

  const root = ReactDOM.createRoot(element.shadowRoot as ShadowRoot);
  const abortController = new AbortController();
  let isMounted = true;

  const renderTree = () => {
    if (!isMounted) {
      return;
    }
    root.render(
      <CacheProvider value={cache}>
        <WidgetRuntimeConfigProvider value={runtimeConfig}>
          <Sentry.ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag("component", `pollen-drawer-ai-widget:${options.componentName}`);
              scope.setContext("widget", {
                shadowDOM: true,
                component: options.componentName,
              });
            }}
          >
            {options.render(runtimeConfig)}
          </Sentry.ErrorBoundary>
        </WidgetRuntimeConfigProvider>
      </CacheProvider>,
    );
  };

  // Initial render (so the widget is visible before the API responds).
  renderTree();

  void fetchWidgetTheme(apiUrl, attributes.apiKey, abortController.signal).then(
    ({
      theme,
      disclaimerText,
      suggestedQuestions,
      placeholderText,
      showChatQuestions,
      showLandingQuestions,
      organizationSlug,
      langfuse,
    }) => {
      if (!isMounted) {
        return;
      }
      applyThemeWithOverrides(element, theme, colorOverrides);

      // Re-initialize the module-level langfuse client per the tenant config.
      // (Multiple widgets on the same page will all share the same client,
      // but they should also resolve to the same tenant from a shared api-key.)
      initializeLangfuseClient(langfuse ?? null, attributes.apiKey);

      // Replace (don't mutate) runtimeConfig so the provider sees a new value.
      // Non-empty HTML content attributes take precedence over tenant config.
      const resolvedContent = resolveWidgetContent({
        inline: inlineContent,
        tenant: { disclaimerText, suggestedQuestions },
        defaultDisclaimerText: DEFAULT_DISCLAIMER_TEXT,
      });
      runtimeConfig = {
        ...runtimeConfig,
        showChatQuestions,
        showLandingQuestions,
        organizationSlug: organizationSlug ?? runtimeConfig.organizationSlug,
        widgetDisclaimerText: resolvedContent.disclaimerText,
        widgetSuggestedQuestions: resolvedContent.suggestedQuestions,
        placeholderText,
        langfuse: langfuse ?? null,
      };

      renderTree();
    },
  );

  return {
    unmount: () => {
      isMounted = false;
      abortController.abort();
      root.unmount();
    },
  };
};
